import { useRef, useEffect, RefObject } from "react";
import { MIN_SCROLLBAR_THUMB_SIZE } from "../utils/timelineConstants";

interface UseTimelineScrollbarProps {
  containerRef: RefObject<HTMLDivElement>;
  onScrollRatioChange?: (ratio: number) => void;
}

export const useTimelineScrollbar = ({ containerRef, onScrollRatioChange }: UseTimelineScrollbarProps) => {
  const barDragRef = useRef<boolean>(false);
  const barStartXRef = useRef<number>(0);
  const barStartLeftRef = useRef<number>(0);

  /**
   * Calculate scrollbar thumb metrics
   */
  const getThumbMetrics = () => {
    const el = containerRef.current;
    if (!el) return { track: 0, thumb: 0, left: 0 };

    const track = el.clientWidth;
    const total = el.scrollWidth;

    if (total <= 0) return { track, thumb: track, left: 0 };

    const thumb = Math.max(MIN_SCROLLBAR_THUMB_SIZE, Math.round((el.clientWidth / total) * track));
    const maxLeft = Math.max(0, track - thumb);
    const left =
      total > el.clientWidth ? Math.round((el.scrollLeft / (total - el.clientWidth)) * maxLeft) : 0;

    return { track, thumb, left };
  };

  /**
   * Start scrollbar drag
   */
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    barDragRef.current = true;
    barStartXRef.current = e.clientX;
    barStartLeftRef.current = getThumbMetrics().left;
    e.preventDefault();
  };

  /**
   * Set up scrollbar drag listeners
   */
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!barDragRef.current) return;

      const el = containerRef.current;
      if (!el) return;

      const { track, thumb } = getThumbMetrics();
      const maxLeft = Math.max(0, track - thumb);
      const dx = e.clientX - barStartXRef.current;
      const newLeft = Math.max(0, Math.min(maxLeft, barStartLeftRef.current + dx));

      const total = el.scrollWidth - el.clientWidth;
      const ratio = maxLeft > 0 ? newLeft / maxLeft : 0;
      const newScroll = Math.round(ratio * total);

      if (el.scrollLeft !== newScroll) {
        el.scrollLeft = newScroll;
        const actualRatio = total > 0 ? newScroll / total : 0;
        onScrollRatioChange?.(actualRatio);
      }
    };

    const onMouseUp = () => {
      if (barDragRef.current) barDragRef.current = false;
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  return {
    getThumbMetrics,
    onMouseDown,
    isDragging: barDragRef.current,
  };
};
